import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { DateInput } from '../../../../shared/components/DateInput.js';
import { UserPicker } from '../../components/UserPicker.js';

interface CapaRow {
  readonly capaId: string;
  readonly capaCode: string;
  readonly capaType: string;
  readonly description: string;
  readonly dueDate: string;
  readonly status: string;
}

export function M21CapaDashboardPage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { day } = useBusinessDay();
  const { token } = useAuth();
  const [rows, setRows] = useState<CapaRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [capaCode, setCapaCode] = useState('');
  const [capaType, setCapaType] = useState('CORRECTIVE');
  const [description, setDescription] = useState('');
  const [actionPlan, setActionPlan] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [dueDate, setDueDate] = useState('');

  function load(): void {
    apiRequest<{ data: CapaRow[] }>('/v1/capa', { token })
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message));
  }

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: CapaRow[] }>('/v1/capa', { token })
      .then((r) => { if (alive) setRows(r.data); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  async function handleCreate(e: FormEvent): Promise<void> {
    e.preventDefault();
    setActionError(null);
    if (!capaCode.trim() || !description.trim() || !actionPlan.trim() || !ownerId.trim() || !dueDate) {
      setActionError(t('m21.capa.validationRequired'));
      return;
    }
    setBusyId('create');
    try {
      await apiRequest('/v1/capa', {
        method: 'POST',
        token,
        body: {
          capaCode: capaCode.trim(),
          capaType,
          description: description.trim(),
          actionPlan: actionPlan.trim(),
          ownerId: ownerId.trim(),
          dueDate,
        },
      });
      setCapaCode(''); setCapaType('CORRECTIVE'); setDescription('');
      setActionPlan(''); setOwnerId(''); setDueDate('');
      load();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  async function closeCapa(id: string): Promise<void> {
    setActionError(null);
    const remarks = window.prompt(t('m21.capa.closurePrompt'));
    if (!remarks || !remarks.trim()) return;
    setBusyId(`close-${id}`);
    try {
      await apiRequest(`/v1/capa/${id}/close`, { method: 'POST', token, body: { closureRemarks: remarks.trim() } });
      load();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  if (error) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('m21.capa.loading')}</p>;
  return (
    <main aria-label="capa-dashboard">
      <h1>{t('m21.capa.title')}</h1>

      <form aria-label="capa-create-form" onSubmit={handleCreate}>
        <label>{t('m21.capa.formCode')}<input aria-label="CAPA code" value={capaCode} onChange={(e) => setCapaCode(e.target.value)} /></label>
        <label>{t('m21.capa.formType')}
          <select aria-label="CAPA type" value={capaType} onChange={(e) => setCapaType(e.target.value)}>
            <option value="CORRECTIVE">CORRECTIVE</option>
            <option value="PREVENTIVE">PREVENTIVE</option>
          </select>
        </label>
        <label>{t('m21.capa.formDescription')}<input aria-label="CAPA description" value={description} onChange={(e) => setDescription(e.target.value)} /></label>
        <label>{t('m21.capa.formActionPlan')}<input aria-label="Action plan" value={actionPlan} onChange={(e) => setActionPlan(e.target.value)} /></label>
        <UserPicker label={t('m21.capa.formOwner')} testId="capa-owner" value={ownerId} onPick={(id) => setOwnerId(id)} />
        <label>{t('m21.capa.formDue')}<DateInput aria-label="Due date" type="date" value={dueDate} onValueChange={(nextValue) => setDueDate(nextValue)} /></label>
        <button type="submit" disabled={busyId === 'create'}>{t('m21.capa.createButton')}</button>
      </form>
      {actionError && <p role="alert">{actionError}</p>}

      <table aria-label="capa-table">
        <thead><tr><th>{t('m21.capa.colCode')}</th><th>{t('m21.capa.colType')}</th><th>{t('m21.capa.colDescription')}</th><th>{t('m21.capa.colDueDate')}</th><th>{t('m21.capa.colStatus')}</th><th>{t('m21.capa.colActions')}</th></tr></thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.capaId}>
              <td>{c.capaCode}</td>
              <td>{c.capaType}</td>
              <td>{c.description}</td>
              <td>{day(c.dueDate)}</td>
              <td>{c.status}</td>
              <td>
                {c.status.toUpperCase() !== 'CLOSED' && (
                  <button
                    type="button"
                    aria-label={`close-${c.capaId}`}
                    disabled={busyId === `close-${c.capaId}`}
                    onClick={() => closeCapa(c.capaId)}
                  >{t('m21.capa.close')}</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
